type PaymentFormStatus =
  | "editing"
  | "invalid"
  | "recovering"
  | "refreshing_quote"
  | "submitting"
  | "succeeded"
  | "sold_out"
  | "unresolved";

export type PaymentFormState = {
  error: string | undefined;
  message: string | undefined;
  status: PaymentFormStatus;
};

export type PaymentFormAction =
  | { type: "field_changed" }
  | { type: "validation_failed" }
  | { type: "quote_refresh_started" }
  | { type: "quote_refresh_succeeded" }
  | { error?: string; type: "quote_refresh_failed" }
  | { type: "submission_started" }
  | { type: "submission_validation_failed" }
  | { type: "quote_changed" }
  | { error: string; type: "retryable_failure" }
  | { error: string; type: "recovery_failed" }
  | { type: "sold_out" }
  | { message: string; type: "succeeded" }
  | { message: string; type: "unresolved" };

export type PaymentUiState =
  | "error"
  | "idle"
  | "invalid"
  | "sold_out"
  | "submitting"
  | "success"
  | "unresolved";

export function createPaymentFormState(
  recoverPayment: boolean
): PaymentFormState {
  return {
    error: undefined,
    message: undefined,
    status: recoverPayment ? "recovering" : "editing",
  };
}

function updateState(
  state: PaymentFormState,
  allowedStatuses: PaymentFormStatus[],
  changes: Partial<PaymentFormState>
) {
  return allowedStatuses.includes(state.status)
    ? { ...state, ...changes }
    : state;
}

export function paymentFormReducer(
  state: PaymentFormState,
  action: PaymentFormAction
): PaymentFormState {
  if (
    state.status === "sold_out" ||
    state.status === "succeeded" ||
    state.status === "unresolved"
  ) {
    return state;
  }

  switch (action.type) {
    case "field_changed":
      return updateState(state, ["editing", "invalid", "refreshing_quote"], {
        error: undefined,
        status: "editing",
      });
    case "validation_failed":
      return updateState(state, ["editing"], { status: "invalid" });
    case "quote_refresh_started":
      return updateState(state, ["editing", "invalid"], {
        status: "refreshing_quote",
      });
    case "quote_refresh_succeeded":
      return updateState(state, ["refreshing_quote"], {
        error: undefined,
        status: "editing",
      });
    case "quote_refresh_failed":
      return updateState(state, ["refreshing_quote"], {
        error: action.error ?? state.error,
        status: "editing",
      });
    case "submission_started":
      return updateState(state, ["editing", "invalid", "refreshing_quote"], {
        error: undefined,
        status: "submitting",
      });
    case "submission_validation_failed":
      return updateState(state, ["submitting"], {
        error: undefined,
        status: "invalid",
      });
    case "quote_changed":
      return updateState(state, ["submitting"], { status: "editing" });
    case "retryable_failure":
      return updateState(state, ["editing", "invalid", "submitting"], {
        error: action.error,
        status: "editing",
      });
    case "recovery_failed":
      return updateState(state, ["recovering"], {
        error: action.error,
        status: "editing",
      });
    case "sold_out":
      return {
        error: undefined,
        message: undefined,
        status: "sold_out",
      };
    case "succeeded":
      return updateState(state, ["recovering", "submitting"], {
        message: action.message,
        status: "succeeded",
      });
    case "unresolved":
      return updateState(state, ["recovering", "submitting"], {
        message: action.message,
        status: "unresolved",
      });
    default:
      return state;
  }
}

export function getPaymentError(state: PaymentFormState) {
  return state.status === "editing" ||
    state.status === "invalid" ||
    state.status === "refreshing_quote"
    ? state.error
    : undefined;
}

export function getPaymentUiState(state: PaymentFormState): PaymentUiState {
  if (state.status === "sold_out") {
    return "sold_out";
  }
  if (state.status === "succeeded") {
    return "success";
  }
  if (state.status === "unresolved") {
    return "unresolved";
  }
  if (state.status === "submitting" || state.status === "recovering") {
    return "submitting";
  }
  if (state.status === "invalid") {
    return "invalid";
  }
  return getPaymentError(state) ? "error" : "idle";
}

export function isPaymentBusy(state: PaymentFormState) {
  return state.status === "submitting";
}
